/**
 * Demo responses showing how the AI service should format responses
 * This file demonstrates the expected JSON structure for different response types
 */

import { RESPONSE_TYPES, ExampleResponses } from '../types/chatbot-response';

/**
 * Demo cart summary response
 */
export const demoCartSummary = {
  agentId: 'frontendAgent',
  sessionId: 'demo-session-123',
  message: 'Here\'s your current cart summary with items from 2 different shops.',
  type: RESPONSE_TYPES.CART_SUMMARY,
  data: {
    totalItems: 3,
    shops: [
      {
        shopId: 'kotn',
        shopName: 'KOTN',
        shopLogo: '/logos/kotn.png',
        items: [
          {
            productId: 'hoodie-001',
            name: 'Unisex French Terry Hoodie',
            image: '/products/hoodie.jpg',
            quantity: 1,
            price: 188.00,
            currency: 'CAD'
          },
          {
            productId: 'tshirt-002',
            name: 'Organic Cotton T-Shirt',
            image: '/products/tshirt.jpg',
            quantity: 1,
            price: 45.00,
            currency: 'CAD'
          }
        ],
        subtotal: 233.00,
        currency: 'CAD'
      },
      {
        shopId: 'fire-belly-tea',
        shopName: 'Fire Belly Tea',
        shopLogo: '/logos/fire-belly-tea.png',
        items: [
          {
            productId: 'tea-blend-001',
            name: 'Premium Earl Grey Blend',
            image: '/products/tea.jpg',
            quantity: 1,
            price: 127.35,
            currency: 'CAD'
          }
        ],
        subtotal: 127.35,
        currency: 'CAD'
      }
    ],
    grandTotal: 360.35,
    currency: 'CAD'
  },
  timestamp: new Date().toISOString()
};

/**
 * Demo order list response
 */
export const demoOrderList = {
  agentId: 'frontendAgent',
  sessionId: 'demo-session-123',
  message: 'Here are your recent orders from the past 30 days.',
  type: RESPONSE_TYPES.ORDER_LIST,
  data: {
    orders: [
      {
        orderId: 'ord_2024_001',
        orderNumber: '#ORD-2024-001',
        date: '2024-01-10T14:30:00Z',
        status: 'Delivered',
        statusColor: '#10B981',
        total: 245.50,
        currency: 'CAD',
        itemCount: 3,
        shopName: 'KOTN',
        previewItems: [
          {
            name: 'Cotton T-Shirt',
            image: '/products/tshirt.jpg',
            quantity: 2
          },
          {
            name: 'Denim Jacket',
            image: '/products/jacket.jpg',
            quantity: 1
          }
        ]
      },
      {
        orderId: 'ord_2024_002',
        orderNumber: '#ORD-2024-002',
        date: '2024-01-08T09:15:00Z',
        status: 'In Transit',
        statusColor: '#F59E0B',
        total: 89.99,
        currency: 'CAD',
        itemCount: 1,
        shopName: 'Fire Belly Tea',
        previewItems: [
          {
            name: 'Tea Starter Kit',
            image: '/products/tea-kit.jpg',
            quantity: 1
          }
        ]
      },
      {
        orderId: 'ord_2024_003',
        orderNumber: '#ORD-2024-003',
        date: '2024-01-05T16:45:00Z',
        status: 'Processing',
        statusColor: '#6B7280',
        total: 156.75,
        currency: 'CAD',
        itemCount: 2,
        shopName: 'KOTN',
        previewItems: [
          {
            name: 'Hoodie',
            image: '/products/hoodie.jpg',
            quantity: 1
          },
          {
            name: 'Sweatpants',
            image: '/products/sweatpants.jpg',
            quantity: 1
          }
        ]
      }
    ],
    pagination: {
      page: 1,
      totalPages: 2,
      totalItems: 5
    }
  },
  timestamp: new Date().toISOString()
};

/**
 * Demo product recommendations response
 */
export const demoProductList = {
  agentId: 'frontendAgent',
  sessionId: 'demo-session-123',
  message: 'Based on your recent purchases, here are some products you might like:',
  type: RESPONSE_TYPES.PRODUCT_LIST,
  data: {
    context: 'Recommended for you based on your order history',
    products: [
      {
        productId: 'prod_001',
        name: 'Unisex French Terry Hoodie',
        description: 'Comfortable and stylish hoodie made from premium French terry cotton.',
        image: '/products/hoodie-large.jpg',
        price: 188.00,
        originalPrice: 220.00,
        currency: 'CAD',
        availability: 'in_stock',
        rating: 4.8,
        reviewCount: 124,
        brand: 'KOTN',
        category: 'Clothing'
      },
      {
        productId: 'prod_002',
        name: 'Organic Cotton T-Shirt',
        description: 'Soft, breathable organic cotton t-shirt in various colors.',
        image: '/products/tshirt-large.jpg',
        price: 45.00,
        currency: 'CAD',
        availability: 'in_stock',
        rating: 4.6,
        reviewCount: 89,
        brand: 'KOTN',
        category: 'Clothing'
      },
      {
        productId: 'prod_003',
        name: 'Premium Earl Grey Tea',
        description: 'Handcrafted Earl Grey blend with bergamot and cornflower petals.',
        image: '/products/earl-grey.jpg',
        price: 32.50,
        currency: 'CAD',
        availability: 'limited',
        rating: 4.9,
        reviewCount: 67,
        brand: 'Fire Belly Tea',
        category: 'Beverages'
      },
      {
        productId: 'prod_004',
        name: 'Denim Jacket',
        description: 'Classic denim jacket with modern fit and sustainable materials.',
        image: '/products/denim-jacket.jpg',
        price: 125.00,
        currency: 'CAD',
        availability: 'out_of_stock',
        rating: 4.4,
        reviewCount: 43,
        brand: 'KOTN',
        category: 'Clothing'
      }
    ]
  },
  timestamp: new Date().toISOString()
};

/**
 * Demo product selection response with variant groups
 */
export const demoProductSelection = {
  agentId: 'frontendAgent',
  sessionId: 'demo-session-123',
  message: 'I found several product options for you. Please select your preferred items from each category.',
  type: RESPONSE_TYPES.PRODUCT_SELECTION,
  data: {
    message: 'I found several product options for you. Please select your preferred items from each category.',
    product: {
      productId: 'office-setup-001',
      name: 'Complete Office Setup',
      description: 'Everything you need for a productive home office.',
      image: '/products/office-setup.jpg',
      basePrice: null,
      currency: 'CAD'
    },
    variantGroups: [
      {
        groupId: 'desk-options',
        message: 'Choose your preferred desk option',
        description: 'Select from our premium desk collection',
        items: [
          {
            itemId: 'standing-desk-pro',
            name: 'Standing Desk Pro',
            description: 'Electric height-adjustable standing desk',
            image: '/products/standing-desk.jpg',
            price: 599.99,
            currency: 'CAD',
            availability: 'in_stock',
            attributes: { type: 'standing', material: 'bamboo', size: '60x30' },
            isRecommended: true
          },
          {
            itemId: 'executive-desk',
            name: 'Executive Desk',
            description: 'Traditional wooden executive desk',
            image: '/products/executive-desk.jpg',
            price: 449.99,
            currency: 'CAD',
            availability: 'in_stock',
            attributes: { type: 'traditional', material: 'oak', size: '72x36' },
            isRecommended: false
          },
          {
            itemId: 'compact-desk',
            name: 'Compact Desk',
            description: 'Space-saving desk for small offices',
            image: '/products/compact-desk.jpg',
            price: 299.99,
            currency: 'CAD',
            availability: 'limited',
            attributes: { type: 'compact', material: 'pine', size: '48x24' },
            isRecommended: false
          }
        ]
      },
      {
        groupId: 'chair-options',
        message: 'Select your ergonomic chair',
        description: 'Comfortable seating for long work sessions',
        items: [
          {
            itemId: 'ergonomic-chair-premium',
            name: 'Ergonomic Chair Premium',
            description: 'Full-featured ergonomic office chair',
            image: '/products/ergonomic-chair.jpg',
            price: 399.99,
            currency: 'CAD',
            availability: 'in_stock',
            attributes: { type: 'ergonomic', features: 'lumbar support, adjustable arms' },
            isRecommended: true
          },
          {
            itemId: 'gaming-chair',
            name: 'Gaming Chair',
            description: 'High-back gaming chair with RGB lighting',
            image: '/products/gaming-chair.jpg',
            price: 299.99,
            currency: 'CAD',
            availability: 'in_stock',
            attributes: { type: 'gaming', features: 'RGB lighting, racing style' },
            isRecommended: false
          },
          {
            itemId: 'basic-office-chair',
            name: 'Basic Office Chair',
            description: 'Simple and functional office chair',
            image: '/products/basic-chair.jpg',
            price: 149.99,
            currency: 'CAD',
            availability: 'in_stock',
            attributes: { type: 'basic', features: 'height adjustable' },
            isRecommended: false
          }
        ]
      },
      {
        groupId: 'accessories',
        message: 'Choose your office accessories',
        description: 'Essential accessories to complete your setup',
        items: [
          {
            itemId: 'monitor-stand',
            name: 'Monitor Stand',
            description: 'Adjustable monitor stand with storage',
            image: '/products/monitor-stand.jpg',
            price: 89.99,
            currency: 'CAD',
            availability: 'in_stock',
            attributes: { type: 'stand', material: 'bamboo', features: 'storage drawer' },
            isRecommended: true
          },
          {
            itemId: 'desk-lamp',
            name: 'LED Desk Lamp',
            description: 'Adjustable LED desk lamp with USB charging',
            image: '/products/desk-lamp.jpg',
            price: 59.99,
            currency: 'CAD',
            availability: 'in_stock',
            attributes: { type: 'lighting', features: 'USB charging, adjustable brightness' },
            isRecommended: false
          },
          {
            itemId: 'cable-organizer',
            name: 'Cable Organizer Kit',
            description: 'Complete cable management solution',
            image: '/products/cable-organizer.jpg',
            price: 24.99,
            currency: 'CAD',
            availability: 'in_stock',
            attributes: { type: 'organization', features: 'cable clips, ties, clips' },
            isRecommended: false
          }
        ]
      }
    ],
    selectionType: 'multiple',
    instructions: 'Select as many items as you want to build your perfect office setup. You can choose from any category.'
  },
  timestamp: new Date().toISOString()
};

/**
 * Demo action buttons response
 */
export const demoActionButtons = {
  agentId: 'frontendAgent',
  sessionId: 'demo-session-123',
  message: 'I found several options for you. What would you like to do next?',
  type: RESPONSE_TYPES.ACTION_BUTTONS,
  data: {
    message: 'Choose an action to continue:',
    buttons: [
      {
        id: 'view_cart',
        label: 'View My Cart',
        action: 'send_message',
        message: 'Show me my cart please',
        style: 'primary',
        icon: 'shopping-cart'
      },
      {
        id: 'track_order',
        label: 'Track Latest Order',
        action: 'send_message',
        message: 'Track my most recent order',
        style: 'secondary',
        icon: 'package'
      },
      {
        id: 'browse_products',
        label: 'Browse Products',
        action: 'link',
        url: '/products',
        style: 'secondary',
        icon: 'external-link'
      },
      {
        id: 'contact_support',
        label: 'Contact Support',
        action: 'link',
        url: '/support',
        style: 'danger',
        icon: 'help'
      }
    ]
  },
  timestamp: new Date().toISOString()
};

/**
 * Function to simulate AI service responses based on user input
 */
export const getSimulatedResponse = (userMessage) => {
  const message = userMessage.toLowerCase();
  
  if (message.includes('cart') || message.includes('shopping')) {
    return demoCartSummary;
  }
  
  if (message.includes('order') && (message.includes('recent') || message.includes('history') || message.includes('list'))) {
    return demoOrderList;
  }
  
  if (message.includes('product') || message.includes('recommend') || message.includes('suggest')) {
    return demoProductList;
  }
  
  if (message.includes('select') || message.includes('variant') || message.includes('size') || message.includes('color') || message.includes('office') || message.includes('setup')) {
    return demoProductSelection;
  }
  
  if (message.includes('help') || message.includes('what can') || message.includes('options')) {
    return demoActionButtons;
  }
  
  // Default text response
  return {
    agentId: 'frontendAgent',
    sessionId: 'demo-session-123',
    message: `I understand you're asking about: "${userMessage}". I can help you with orders, products, cart management, and more. Try asking about your cart, recent orders, or product recommendations!`,
    type: RESPONSE_TYPES.TEXT,
    data: {
      message: `I understand you're asking about: "${userMessage}". I can help you with orders, products, cart management, and more. Try asking about your cart, recent orders, or product recommendations!`,
      formatting: 'plain'
    },
    timestamp: new Date().toISOString()
  };
};
